import { DeleteOutlined, ColumnHeightOutlined, UploadOutlined } from "@ant-design/icons";
import { PictureOutlined } from "@ant-design/icons";
import { Button, Form, Input, Modal, Select, Tooltip, FormInstance } from "antd";
import { RcFile } from "antd/lib/upload";
import React, { useState } from "react";

import { ExistingImageSelectorModal } from "../../image-manager/ExistingImageSelectorModal";
import { getContentToDisplay } from "./content-form-util";

type ContentFormItemProps = {
  selectedTagIndex: number;
  form: FormInstance;
  entry: any;
};

export const ContentFormItem = ({
  selectedTagIndex,
  entry,
  form,
  ...props
}: ContentFormItemProps) => {
  const [existingImageModalOpen, setExistingImageModalOpen] = useState(false);
  const [dimensionModalOpen, setDimensionModalOpen] = useState(false);
  const [pendingFile, setPendingFile] = useState<RcFile | null>(null);
  const [imageDimensions, setImageDimensions] = useState<{ width: string; height: string }>({
    width: "100",
    height: "100",
  });
  const [unit, setUnit] = useState<"px" | "pt" | "%">("px");

  const handleUpload = async (
    file: RcFile,
    formKey: string[],
    width?: string,
    height?: string
  ): Promise<string> => {
    return new Promise((resolve) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const base64 = e.target?.result as string;
        const encodedWithName = `height=${height};width=${width};filename:${file.name};data:${
          base64.split(",")[1]
        }`;
        form.setFieldValue([...formKey, "contents"], encodedWithName);
        setImageDimensions({ width: String(width), height: String(height) });
        resolve(encodedWithName);
      };
      reader.readAsDataURL(file);
    });
  };

  return (
    <>
      <Form.Item
        rules={[{ required: true, message: "Content is required" }]}
        noStyle
        shouldUpdate={(prev, curr) => {
          const prevValue = prev.tags?.[selectedTagIndex]?.entries?.[entry.name]?.contentType;
          const currValue = curr.tags?.[selectedTagIndex]?.entries?.[entry.name]?.contentType;

          if (prevValue !== currValue) {
            const formKey = ["tags", selectedTagIndex, "entries", entry.name];
            if (
              ((prevValue === "Text" || prevValue === "HTML") && currValue === "Image") ||
              (prevValue === "Image" && currValue !== "Image")
            ) {
              form.setFieldValue([...formKey, "contents"], "");
            }
          }

          return true;
        }}
        {...props}
      >
        {({ getFieldValue, setFieldValue }) => {
          const formKey = ["tags", selectedTagIndex, "entries", entry.name];
          const contentType = getFieldValue([...formKey, "contentType"]);
          const content = getFieldValue([...formKey, "contents"]);

          const { contentImageStringType, contentDisplay } = getContentToDisplay(content);
          return (
            <Form.Item
              name={[entry.name, "contents"]}
              rules={[{ required: true, message: "Content is required" }]}
              style={{ width: "300px" }}
            >
              {contentType === "Image" ? (
                <div>
                  {content ? (
                    <div style={{ display: "flex", alignItems: "center" }}>
                      <Tooltip
                        title={
                          contentImageStringType === "url" || contentImageStringType === "html" ? (
                            <img height={100} width={100} src={content} alt="image" />
                          ) : (
                            content
                          )
                        }
                      >
                        <div
                          style={{
                            width: "300px",
                            height: "32px",
                            fontSize: "12px",
                            textOverflow: "ellipsis",
                            overflow: "hidden",
                            whiteSpace: "nowrap",
                            alignItems: "center",
                            display: "flex",
                            padding: "0 8px",
                            backgroundColor: "#f5f5f5",
                            border: "1px solid #d9d9d9",
                            borderRadius: "4px",
                          }}
                        >
                          {contentDisplay}
                        </div>
                      </Tooltip>
                      <ColumnHeightOutlined
                        onClick={() => {
                          if (contentImageStringType === "url") {
                            const url = new URL(content);
                            const width = String(url.searchParams.get("width"));
                            const height = String(url.searchParams.get("height"));

                            const { value: widthValue, unit: widthUnit } = parseValueAndUnit(width);
                            const { value: heightValue } = parseValueAndUnit(height);

                            setImageDimensions({ width: widthValue, height: heightValue });
                            setUnit(widthUnit);
                          }
                          if (contentImageStringType === "html") {
                            const dimensions = getImageDimensionsFromHtml(content);
                            const width = dimensions.width ?? "100";
                            const height = dimensions.height ?? "100";

                            const { value: widthValue, unit: widthUnit } = parseValueAndUnit(width);
                            const { value: heightValue } = parseValueAndUnit(height);

                            setImageDimensions({ width: widthValue, height: heightValue });
                            setUnit(widthUnit);
                          }
                          if (contentImageStringType === "base64") {
                            const match = content?.match(/height=(\d+);width=(\d+)/);
                            const height = match ? match[1] : "100";
                            const width = match ? match[2] : "100";

                            setImageDimensions({ width, height });
                            setUnit("px");
                          }
                          setDimensionModalOpen(true);
                        }}
                        style={{ marginLeft: "8px" }}
                      />
                      <DeleteOutlined
                        onClick={() => {
                          Modal.confirm({
                            title: "Are you sure you want to remove this image?",
                            onOk: () => setFieldValue([...formKey, "contents"], null),
                          });
                        }}
                        style={{ marginLeft: "8px" }}
                      />
                    </div>
                  ) : (
                    <Button.Group>
                      <ExistingImageSelectorModal
                        onSelect={(image) => {
                          const appenedQueryParams = `${image.url}?width=${image.width}&height=${image.height}`;
                          setFieldValue([...formKey, "contents"], appenedQueryParams);
                          setExistingImageModalOpen(false);
                        }}
                        open={existingImageModalOpen}
                        onCancel={() => setExistingImageModalOpen(false)}
                      />
                      <Button
                        icon={<PictureOutlined />}
                        onClick={() => {
                          setExistingImageModalOpen(true);
                        }}
                      >
                        Existing
                      </Button>
                      <Button
                        style={{ width: "50px" }}
                        icon={<UploadOutlined />}
                        onClick={() => {
                          const fileInput = document.createElement("input");
                          fileInput.type = "file";
                          fileInput.accept = "image/*";
                          fileInput.onchange = async (e) => {
                            const file = (e.target as HTMLInputElement).files?.[0];
                            if (file) {
                              setPendingFile(file as RcFile);
                              setDimensionModalOpen(true);
                            }
                          };
                          fileInput.click();
                        }}
                      />
                    </Button.Group>
                  )}
                </div>
              ) : (
                <div
                  style={{
                    cursor: "pointer",
                    border: "1px solid #d9d9d9",
                    borderRadius: "6px",
                    padding: "4px 11px",
                    backgroundColor: "#fff",
                    overflow: "hidden",
                    height: "32px",
                  }}
                  onClick={() => {
                    const target = getFieldValue(formKey);
                    Modal.info({
                      title: "Edit Content",
                      width: 800,
                      style: {
                        height: "80vh",
                      },
                      content: (
                        <div style={{ minHeight: "200px" }}>
                          <Input.TextArea
                            rows={10}
                            defaultValue={target?.contents ?? ""}
                            onBlur={(e) => {
                              console.log("ONBLUR");
                              const value = e.target.value;
                              setFieldValue([...formKey, "contents"], value);
                            }}
                          />
                        </div>
                      ),
                      icon: null,
                    });
                  }}
                >
                  {content}
                </div>
              )}
            </Form.Item>
          );
        }}
      </Form.Item>
      <Modal
        title="Set Image Dimensions"
        open={dimensionModalOpen}
        onOk={async () => {
          const currentContent = form.getFieldValue([
            "tags",
            selectedTagIndex,
            "entries",
            entry.name,
            "contents",
          ]);
          const contentImageStringType = getContentToDisplay(currentContent).contentImageStringType;

          const widthWithUnit = `${imageDimensions.width}${unit}`;
          const heightWithUnit = `${imageDimensions.height}${unit}`;

          if (contentImageStringType === "url") {
            const newurl = `${currentContent}?width=${widthWithUnit}&height=${heightWithUnit}`;
            form.setFieldValue(
              ["tags", selectedTagIndex, "entries", entry.name, "contents"],
              newurl
            );
          }
          if (contentImageStringType === "html") {
            const newHtml = currentContent.replace(
              /width="[^"]+" height="[^"]+"/,
              `width="${widthWithUnit}" height="${heightWithUnit}"`
            );
            form.setFieldValue(
              ["tags", selectedTagIndex, "entries", entry.name, "contents"],
              newHtml
            );
          }

          if (pendingFile) {
            await handleUpload(
              pendingFile,
              ["tags", selectedTagIndex, "entries", entry.name],
              widthWithUnit,
              heightWithUnit
            );
            setPendingFile(null);
          }
          setDimensionModalOpen(false);
        }}
        onCancel={() => {
          setPendingFile(null);
          setDimensionModalOpen(false);
        }}
        okText="Ok"
      >
        <Form layout="vertical">
          <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-start" }}>
              <Form.Item
                label="Width"
                name="dimensionWidth"
                initialValue={imageDimensions.width}
                style={{ marginBottom: 0, flex: 1 }}
                rules={[
                  { required: true, message: "Width is required" },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const numberRegex = /^\d+(\.\d+)?$/;

                      if (numberRegex.test(value)) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error("Must be a number"));
                    },
                  },
                ]}
              >
                <Input
                  min={"1"}
                  value={imageDimensions.width}
                  onChange={(e) => setImageDimensions((d) => ({ ...d, width: e.target.value }))}
                />
              </Form.Item>
              <Form.Item
                label="Height"
                name="dimensionHeight"
                initialValue={imageDimensions.height}
                style={{ marginBottom: 0, flex: 1 }}
                rules={[
                  { required: true, message: "Height is required" },
                  {
                    validator: (_, value) => {
                      if (!value) {
                        return Promise.resolve();
                      }

                      const numberRegex = /^\d+(\.\d+)?$/;

                      if (numberRegex.test(value)) {
                        return Promise.resolve();
                      }

                      return Promise.reject(new Error("Must be a number"));
                    },
                  },
                ]}
              >
                <Input
                  min={"1"}
                  value={imageDimensions.height}
                  onChange={(e) => setImageDimensions((d) => ({ ...d, height: e.target.value }))}
                />
              </Form.Item>
            </div>
            <Form.Item label="Unit" style={{ marginBottom: 0 }}>
              <Select
                value={unit}
                onChange={setUnit}
                options={[
                  { label: "px", value: "px" },
                  { label: "pt", value: "pt" },
                  { label: "%", value: "%" },
                ]}
              />
            </Form.Item>
          </div>
        </Form>
      </Modal>
    </>
  );
};

function getImageDimensionsFromHtml(htmlString: string): { width?: string; height?: string } {
  const tempDiv = document.createElement("div");
  tempDiv.innerHTML = htmlString;
  const imgElement = tempDiv.querySelector("img");
  const width = imgElement?.getAttribute("width");
  const height = imgElement?.getAttribute("height");
  return {
    width: width ?? undefined,
    height: height ?? undefined,
  };
}

function parseValueAndUnit(dimension: string): { value: string; unit: "px" | "pt" | "%" } {
  const match = dimension.match(/^(\d+(?:\.\d+)?)(%|px|pt)?$/);
  if (match) {
    const value = match[1];
    const unit = (match[2] as "px" | "pt" | "%") || "px";
    return { value, unit };
  }
  return { value: dimension, unit: "px" };
}
